/**
 * Hook para gerenciamento de produtos CRM
 * Fornece queries e mutations para CRUD de produtos e serviços
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { CrmProductCategory } from './useCrmCategories';
import type {
  RecurrenceInterval,
  TermType,
  TermUnit,
} from '@/lib/crm/recurringServiceUtils';

// Tipo para produto
export interface CrmProduct {
  id: string;
  tenant_id: string;
  category_id: string | null;
  name: string;
  description: string | null;
  sku: string | null;
  product_type: 'product' | 'service';
  unit_type: string;
  default_price: number;
  cost_price: number | null;
  currency: string;
  is_active: boolean;
  custom_fields: Record<string, unknown>;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  category?: CrmProductCategory;
  // Campos de serviço recorrente (NULL quando não aplicável)
  recurring_price: number | null;
  recurrence_interval: RecurrenceInterval | null;
  term_type: TermType | null;
  term_quantity: number | null;
  term_unit: TermUnit | null;
  /**
   * Preço aberto (valor sob demanda): quando true, o item não tem valor fixo
   * e o preço é informado manualmente na criação da cobrança/proposta.
   */
  is_open_price: boolean;
}

// Tipo para criação de produto
export interface CreateProductInput {
  name: string;
  description?: string;
  sku?: string;
  category_id?: string | null;
  product_type: 'product' | 'service';
  unit_type?: string;
  default_price: number;
  cost_price?: number;
  currency?: string;
  custom_fields?: Record<string, unknown>;
  // Recorrentes (apenas quando service + recorrente)
  recurring_price?: number | null;
  recurrence_interval?: RecurrenceInterval | null;
  term_type?: TermType | null;
  term_quantity?: number | null;
  term_unit?: TermUnit | null;
  /** Preço aberto: valor definido na cobrança (sem preço fixo). */
  is_open_price?: boolean;
}

// Tipo para atualização de produto
export interface UpdateProductInput {
  id: string;
  name?: string;
  description?: string | null;
  sku?: string | null;
  category_id?: string | null;
  product_type?: 'product' | 'service';
  unit_type?: string;
  default_price?: number;
  cost_price?: number | null;
  currency?: string;
  is_active?: boolean;
  custom_fields?: Record<string, unknown>;
  // Recorrentes
  recurring_price?: number | null;
  recurrence_interval?: RecurrenceInterval | null;
  term_type?: TermType | null;
  term_quantity?: number | null;
  term_unit?: TermUnit | null;
  /** Preço aberto: valor definido na cobrança (sem preço fixo). */
  is_open_price?: boolean;
}

// Filtros para listagem
export interface ProductFilters {
  product_type?: 'product' | 'service' | null;
  /** ID da categoria ou array de IDs (para incluir subcategorias) */
  category_id?: string | string[] | null;
  is_active?: boolean | null;
  search?: string;
  /** Filtrar por workspace (via tabela de junção crm_product_workspaces) */
  workspace_id?: string | null;
  /** Filtra pelo tipo de unidade (ex.: 'recorrente'). Usado pelo modal de Nova Recorrência. */
  unit_type?: string | null;
  /** Filtra serviços recorrentes pela periodicidade (mensal, trimestral, anual, etc.). */
  recurrence_interval?: RecurrenceInterval | null;
}


// Opções de configuração do hook
export interface ProductsOptions {
  enabled?: boolean;
}

/**
 * Hook principal para produtos CRM
 * Suporta lazy loading via options.enabled
 */
export function useCrmProducts(filters?: ProductFilters, options?: ProductsOptions) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();

  // Determina se query deve executar (lazy loading)
  const isEnabled = (options?.enabled ?? true) && !!currentTenant?.id;

  // Query para listar produtos via RPC segura
  const productsQuery = useQuery({
    queryKey: ['crm-products', currentTenant?.id, filters],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      // Montar parâmetros da RPC
      const rpcParams: Record<string, unknown> = {
        p_tenant_hint: currentTenant.id,
      };

      // Filtro por tipo de produto
      if (filters?.product_type) {
        rpcParams.p_product_type = filters.product_type;
      }

      // Filtro por categoria (ID único ou array de IDs)
      if (filters?.category_id) {
        if (Array.isArray(filters.category_id)) {
          rpcParams.p_category_ids = filters.category_id;
        } else {
          rpcParams.p_category_id = filters.category_id;
        }
      }

      // Filtro por status ativo
      if (filters?.is_active !== null && filters?.is_active !== undefined) {
        rpcParams.p_is_active = filters.is_active;
      }

      // Filtro por busca textual
      if (filters?.search) {
        rpcParams.p_search = filters.search;
      }

      // Filtro por workspace
      if (filters?.workspace_id) {
        rpcParams.p_workspace_id = filters.workspace_id;
      }

      // Filtro por unit_type (ex.: 'recorrente')
      if (filters?.unit_type) {
        rpcParams.p_unit_type = filters.unit_type;
      }

      // Filtro por periodicidade (apenas serviços recorrentes)
      if (filters?.recurrence_interval) {
        rpcParams.p_recurrence_interval = filters.recurrence_interval;
      }


      const { data, error } = await supabase.rpc('get_tenant_products_safe', rpcParams);

      if (error) throw error;

      // Mapear retorno da RPC para interface CrmProduct
      return (data || []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        tenant_id: '', // Não exposto pela RPC
        category_id: (row.category_id as string) || null,
        name: row.name as string,
        description: (row.description as string) || null,
        sku: (row.sku as string) || null,
        product_type: row.product_type as 'product' | 'service',
        unit_type: row.unit_type as string,
        default_price: row.default_price as number,
        cost_price: (row.cost_price as number) || null,
        currency: row.currency as string,
        is_active: row.is_active as boolean,
        custom_fields: {},
        metadata: {},
        created_by: (row.created_by_short as string) || null,
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
        // Recorrentes (vindos da RPC)
        recurring_price: (row.recurring_price as number) ?? null,
        recurrence_interval: (row.recurrence_interval as RecurrenceInterval) ?? null,
        term_type: (row.term_type as TermType) ?? null,
        term_quantity: (row.term_quantity as number) ?? null,
        term_unit: (row.term_unit as TermUnit) ?? null,
        // Preço aberto (valor definido na cobrança)
        is_open_price: (row.is_open_price as boolean) ?? false,
        // Categoria aninhada (compatibilidade com interface existente)
        category: row.category_name ? {
          id: row.category_id as string,
          tenant_id: '',
          parent_id: null,
          name: row.category_name as string,
          color: (row.category_color as string) || null,
          icon: (row.category_icon as string) || null,
          sort_order: 0,
          is_active: true,
          created_at: '',
          updated_at: '',
        } : undefined,
      })) as CrmProduct[];
    },
    enabled: isEnabled,
    staleTime: 30 * 1000, // Cache de 30 segundos
  });

  // Mutation para criar produto
  const createProduct = useMutation({
    mutationFn: async (input: CreateProductInput) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      const { data, error } = await supabase
        .from('crm_products')
        .insert({
          tenant_id: currentTenant.id,
          name: input.name,
          description: input.description || null,
          sku: input.sku || null,
          category_id: input.category_id || null,
          product_type: input.product_type,
          unit_type: input.unit_type || 'un',
          default_price: input.default_price,
          cost_price: input.cost_price || null,
          currency: input.currency || 'BRL',
          custom_fields: input.custom_fields ? JSON.parse(JSON.stringify(input.custom_fields)) : {},
          // Campos de serviço recorrente — trigger no banco valida/normaliza
          recurring_price: input.recurring_price ?? null,
          recurrence_interval: input.recurrence_interval ?? null,
          term_type: input.term_type ?? null,
          term_quantity: input.term_quantity ?? null,
          term_unit: input.term_unit ?? null,
          // Preço aberto (valor sob demanda)
          is_open_price: input.is_open_price ?? false,
          created_by: user?.id,
          // Cast: coluna is_open_price recém-criada (types.ts regenerado pelo Supabase)
        } as never)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-products'] });
      toast.success('Produto criado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar produto: ${error.message}`);
    },
  });

  // Mutation para atualizar produto
  const updateProduct = useMutation({
    mutationFn: async (input: UpdateProductInput) => {
      const { id, custom_fields, ...restUpdates } = input;
      
      // Preparar updates com custom_fields tratado corretamente
      const updates = {
        ...restUpdates,
        ...(custom_fields !== undefined && { custom_fields: JSON.parse(JSON.stringify(custom_fields)) }),
      };

      const { data, error } = await supabase
        .from('crm_products')
        // Cast: coluna is_open_price recém-criada (types.ts regenerado pelo Supabase)
        .update(updates as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-products'] });
      toast.success('Produto atualizado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar produto: ${error.message}`);
    },
  });

  // Mutation para deletar produto
  const deleteProduct = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_products')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-products'] });
      toast.success('Produto excluído com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir produto: ${error.message}`);
    },
  });

  return {
    products: productsQuery.data || [],
    isLoading: productsQuery.isLoading,
    error: productsQuery.error,
    createProduct,
    updateProduct,
    deleteProduct,
    refetch: productsQuery.refetch,
  };
}
